import type { ExportMode, ExportOptions, NetPayDetailMode, OptionSource, WageGroupDimension, WageMappingMode } from './exportTypes';

/**
 * Words for the export options (docs/payroll-accounting-export-mapping-options-contract.md sections 1 to 3). The two net pay explanations are the contract's
 * exact copy and must not be reworded. Everything else here is plain-language help: it explains what a choice does, never promises what the server decides.
 */

/** Contract section 2, verbatim. */
export const NET_PAY_COPY: Record<NetPayDetailMode, string> = {
  PerEmployee: "Creates a separate Payroll Clearing line for each employee's net pay. This provides greater payment-level detail.",
  Consolidated: "Combines all employee net pay into one Payroll Clearing line. Employee detail remains available in EPN's payroll reports.",
};

/** The two radio labels differ from the Export mode radios ("Per employee") on purpose: two controls with the same name would be confusing to read aloud. */
export const NET_PAY_CHOICE_LABEL: Record<NetPayDetailMode, string> = {
  PerEmployee: 'One net pay line for each employee',
  Consolidated: 'One consolidated net pay line',
};

/** Short forms for tables, summaries and the settings select. */
export const NET_PAY_SHORT_LABEL: Record<NetPayDetailMode, string> = { PerEmployee: 'Per employee', Consolidated: 'Consolidated' };

export const WAGE_MODE_LABEL: Record<WageMappingMode, string> = {
  SingleAccount: 'Single account',
  GroupBased: 'By group',
  LegacyResolver: 'Legacy rules (as before this option existed)',
};

export const WAGE_MODE_COPY: Record<WageMappingMode, string> = {
  SingleAccount:
    'All gross wages post to the company default Wage Expense account (an earning-code mapping is used first if one exists). Employee, group, department and location wage mappings are not used, so no group mapping can override the common account.',
  GroupBased:
    'Each employee\'s gross wages post to the account mapped to their group in the dimension you choose. An employee whose group has no mapping blocks the export (the default account is never substituted silently), and so does an employee with no group. An employee-specific override still applies first.',
  LegacyResolver:
    'The original rules: an employee override, then a payroll-code mapping, then department, then location (the payroll state), then the company default.',
};

/** "Work location" is the employee's primary work location; the legacy "location" mapping qualifier is the payroll state (tax jurisdiction): they are different. */
export const DIMENSION_LABEL: Record<WageGroupDimension, string> = {
  Department: 'Department',
  WorkLocation: 'Work location',
  Role: 'Role',
  Discipline: 'Discipline',
  EmploymentType: 'Employment type',
  EmployeeGroup: 'Employee group',
};

export const DIMENSION_HELP: Record<WageGroupDimension, string> = {
  Department: "The employee's department.",
  WorkLocation: "The employee's primary work location. This is not the state (tax jurisdiction) the payroll carries.",
  Role: "The employee's job role.",
  Discipline: "The employee's healthcare discipline, where one is set.",
  EmploymentType: "The employee's employment type, for example full-time or part-time.",
  EmployeeGroup: 'A group your company defines in GL Mapping. An employee must be in exactly one active group on the accounting date.',
};

export const SOURCE_LABEL: Record<OptionSource, string> = {
  Request: 'Chosen for this export',
  CompanySettings: 'Company setting',
  LegacyDefault: 'Legacy / default',
};

export const dimensionLabel = (d: WageGroupDimension | null | undefined): string => (d ? DIMENSION_LABEL[d] : '');

/** Group attributes are the employee's current values, not effective-dated to the check date (a documented limitation of the contract). */
export const DIMENSION_LIMITATION = 'Group attributes are the employee\'s current values, not the values on the check date.';

export const LEGACY_LABEL = 'Legacy / default';
export const LEGACY_EXPLANATION =
  'This export was recorded before export options existed, or no option was set. It follows the original rules: one summarized net pay credit per mapped account and the original wage mapping order.';

/** What the options say, in words, for a summary line. A legacy record is always labelled as such (never shown as if it were a chosen setting). */
export function describeNetPay(options: ExportOptions, mode: ExportMode): string {
  if (mode === 'PerEmployee') return 'Per employee (every line is kept per employee)';
  if (!options.recorded || options.source === 'LegacyDefault' || !options.netPayDetailMode) return `${LEGACY_LABEL}: one summarized credit per mapped account`;
  return NET_PAY_SHORT_LABEL[options.netPayDetailMode];
}

export function describeWage(options: ExportOptions): string {
  if (!options.recorded || options.source === 'LegacyDefault' || !options.wageMappingMode || options.wageMappingMode === 'LegacyResolver') return `${LEGACY_LABEL}: original mapping order`;
  if (options.wageMappingMode === 'GroupBased') return `By group (${dimensionLabel(options.wageGroupDimension) || 'no dimension'})`;
  return 'Single account';
}

export const isLegacyOptions = (options: ExportOptions): boolean => !options.recorded || options.source === 'LegacyDefault';

/** The blocker codes added by the mapping options, with a title and a plain explanation of how to fix them. The server's own message (with counts and groups) is shown too. */
export const OPTION_BLOCKERS: Record<string, { title: string; help: string }> = {
  NetPayNotConsolidatable: {
    title: 'Net pay cannot be consolidated',
    help: 'Consolidated net pay needs every employee\'s net pay to resolve to one Payroll Clearing account. Some resolve to different accounts, and EPN never merges amounts that belong to different accounts. Choose one net pay line for each employee, or end the extra net pay mappings so one account remains.',
  },
  DefaultWageAccountRequired: {
    title: 'A default wage account is required',
    help: 'Single account mode posts all wages to the company default Wage Expense account, and none is active. Add a company-default Gross wages mapping in GL Mapping.',
  },
  WageGroupDimensionRequired: {
    title: 'Choose a group dimension',
    help: 'By group mode needs to know what to group by (department, work location, role, discipline, employment type or employee group). Choose one, then preview again.',
  },
  GroupMappingMissing: {
    title: 'A group has no wage account',
    help: 'Employees in the groups listed have no Gross wages mapping for their group, and no employee override. The default account is not used silently. Assign an account to each group in GL Mapping, under By group.',
  },
  GroupMembershipMissing: {
    title: 'Employees without a group',
    help: 'Employees listed have no value for the chosen dimension, so EPN cannot tell which account their wages belong to. Set their department, location, role, discipline, employment type or employee group, or add an employee override.',
  },
  EmployeeGroupAmbiguous: {
    title: 'Employee is in more than one group',
    help: 'An employee belongs to more than one active employee group on the accounting date. End one of the memberships in GL Mapping, under Employee groups.',
  },
  MappingTenantMismatch: {
    title: 'A mapping does not belong to this company',
    help: 'A journal line used a mapping or account that is not this company\'s. Nothing was exported. This should not happen: contact support with the reference shown on the page.',
  },
};

export const optionBlockerTitle = (code: string): string | null => OPTION_BLOCKERS[code]?.title ?? null;
export const optionBlockerHelp = (code: string): string | null => OPTION_BLOCKERS[code]?.help ?? null;

/** Warning codes added by the options. */
export const OPTION_WARNING_HELP: Record<string, string> = {
  CodesWithoutMapping:
    'Some deduction codes have no GL mapping yet. This does not block the export: the journal posts the three deduction totals under their category accounts today. Code mappings are recorded for when the payroll supplies per-code amounts.',
};

export const CODE_MAPPING_BANNER =
  'Mappings saved here are recorded and audited, but the journal cannot use them until the payroll supplies per-code amounts. Today the journal posts the three deduction totals under their category accounts.';
